import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  Grid,
  Divider,
  Avatar,
  IconButton,
  Tooltip
} from "@mui/material";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import 'react-toastify/dist/ReactToastify.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import EditIcon from "@mui/icons-material/Edit";
import baseURL from "../../config";
import Navbar from "../../Navbar/Navbar";

const Seaservicecrewing = () => {
  const { id: crewingAgentId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    address: "",
    mobileNumber: "",
    email: "",
    authorisedSignatoryName: "",
    logo: null,
    existingLogoUrl: ""
  });

  // Fetch profile
  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${baseURL}/api/getSeaServiceProfile/${crewingAgentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const responseData = res.data?.data;
      if (!responseData) {
        setProfileData(null);
      } else {
        setProfileData(responseData);
      }
    } catch (err) {
      setProfileData(null);
    } finally {
      setLoading(false);
    }
  };

  // Set form data in edit mode
  useEffect(() => {
    if (editMode && profileData) {
      setFormData({
        companyName: profileData.companyName || "",
        address: profileData.address || "",
        mobileNumber: profileData.mobileNumber || "",
        email: profileData.email || "",
        authorisedSignatoryName: profileData.authorisedSignatoryName || "",
        logo: null,
        existingLogoUrl: profileData.logo?.filePath || ""
      });
    }
  }, [editMode, profileData]);

  useEffect(() => {
    fetchProfile();
  }, [crewingAgentId]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, mobileNumber: `+${value}` }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only PNG and JPG files are allowed.");
        return;
      }
    }
    setFormData((prev) => ({ ...prev, logo: file }));
  };

  // Handle submit (create/update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append("companyName", formData.companyName);
      data.append("address", formData.address);
      data.append("email", formData.email);
      data.append("mobileNumber", formData.mobileNumber);
      data.append("authorisedSignatoryName", formData.authorisedSignatoryName);
      if (formData.logo) data.append("logo", formData.logo);

      const token = localStorage.getItem("token");
      const url = profileData
        ? `${baseURL}/api/updateSeaServiceProfile/${profileData.crewingAgentId}`
        : `${baseURL}/api/createSeaServiceProfile/${crewingAgentId}`;

      const res = await axios[profileData ? "put" : "post"](url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });

      toast.success(res.data.message || "Profile saved successfully");
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving profile");
    } finally {
      setLoading(false);
    }
  };

  // Loader
  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* <Navbar /> */}
      <Box sx={{ mt: 2 }}>
        {editMode || !profileData ? (
          <Box
            component={Paper}
            elevation={3}
            sx={{
              maxWidth: 400,
              mx: "auto",
              p: 2,
              borderRadius: 3
            }}
          >
            <Typography variant="h6" mb={3} fontWeight={600} textAlign="center">
              {profileData ? "Edit Sea Service Profile" : "Create Sea Service Profile"}
            </Typography>

            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <Stack spacing={1}>
                <TextField
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  fullWidth
                />

                <TextField
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  fullWidth
                />

                <TextField
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  fullWidth
                />

                <PhoneInput
                  country={"in"}
                  value={formData.mobileNumber.replace("+", "")}
                  onChange={handlePhoneChange}
                  inputStyle={{ width: "100%" }}
                  specialLabel="Mobile Number"
                  inputProps={{ required: true }}
                />

                <TextField
                  label="Authorized Signatory Full Name"
                  name="authorisedSignatoryName"
                  value={formData.authorisedSignatoryName}
                  onChange={handleChange}
                  required
                  fullWidth
                />

                <Stack spacing={1}>
                  <Button variant="outlined" component="label" fullWidth color="warning">
                    Upload Logo (png, jpg)
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  {formData.logo && (
                    <Typography variant="body2" color="text.secondary">
                      Selected File: <strong>{formData.logo.name}</strong>
                    </Typography>
                  )}
                </Stack>

                {formData.existingLogoUrl && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">
                      Existing Logo
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  color="error"
                  type="submit"
                  disabled={loading}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  {loading ? "Submitting..." : "Save Profile"}
                </Button>
              </Stack>
            </form>
          </Box>
        ) : (
          <Box
            component={Paper}
            elevation={4}
            sx={{
              p: 4,
              maxWidth: 700,
              mx: "auto",
              borderRadius: 3,
              position: "relative"
            }}
          >
            <Tooltip title="Edit Profile">
              <IconButton
                onClick={() => setEditMode(true)}
                sx={{ position: "absolute", top: 16, right: 16 }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Typography variant="h5" fontWeight="bold" gutterBottom textAlign="center">
              Sea Service Profile
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company Name
                </Typography>
                <Typography fontWeight={500}>{profileData.companyName}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Authorized Signatory
                </Typography>
                <Typography fontWeight={500}>
                  {profileData.authorisedSignatoryName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mobile Number
                </Typography>
                <Typography fontWeight={500}>{profileData.mobileNumber}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Address
                </Typography>
                <Typography fontWeight={500}>{profileData.address}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography fontWeight={500}>{profileData.email}</Typography>
              </Grid>

              {profileData.logo?.filePath && (
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Company Logo
                  </Typography>
                  <Avatar
                    alt="Logo"
                    src={`${baseURL}/${profileData.logo.filePath}`}
                    variant="rounded"
                    sx={{ width: 120, height: 120, mx: "auto" }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Box>

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default Seaservicecrewing;

